/**
 * `Pads` — the sample bank and the dub siren. `docs/design-direction.md` §9.9,
 * `spec.md` §13.10 (`/pads`).
 *
 * Sixteen pads fire one-shots **over the track that is already playing**, and a
 * press-and-hold siren rides on top of it. Long-press a pad to swap its sound out of the
 * server's CC0 library; the bank saves per Telegram account, the same way MY CRATE does.
 *
 * Seven things here are load-bearing.
 *
 *  1. **The engine is created inside the first `pointerdown`, synchronously.**
 *     `ensurePadEngine()` is the first statement of every pointer handler and nothing
 *     awaits above it — `engine/unlock.ts`'s header explains why the order is
 *     load-bearing for the media element, and a webview grants an `AudioContext` the same
 *     way. It is *not* created on mount: a context built without a gesture starts
 *     suspended and, on iOS, may take an audio session category that silences it.
 *  2. **This screen owns the vertical-swipe state.** `main.tsx:348` disables swipes once
 *     at boot, but `Scrub.tsx`, `NowPlaying.tsx` and `SetList.tsx` each hand the gesture
 *     back on release and nothing re-issues the disable, so it is live again within a
 *     minute of use. On a press-and-hold pad a thumb drifting 8px down dismisses the Mini
 *     App, `pointerup` never arrives, and the held siren sticks on. So: disable on mount
 *     **and on every `pointerdown`**, and restore only on unmount. `touch-action: none`
 *     does not help — it stops the browser scrolling, not Telegram's host-side gesture.
 *  3. **`pointerdown`, never `click`.** `click` adds the pointerup→click gap, which is
 *     50–80 ms of latency on the one interaction the whole screen is judged by. This is
 *     the house pattern already (`Scrub.tsx`, the disc drag).
 *  4. **A hit never goes through React state before it sounds.** `firePad` is called
 *     first and the lit class is written afterwards; the pad's ink is a consequence of
 *     the sound, not a step on the way to it. The ink is held for the sound's **own**
 *     measured duration, so the light is the length of the sound.
 *  5. **Pointer capture, and four exit paths.** A held pad or siren releases on
 *     `pointerup`, `pointercancel`, `lostpointercapture` and unmount. Three of those are
 *     not paranoia: Telegram can steal the pointer, the webview can background mid-hold,
 *     and a navigation can unmount the screen with a finger still down.
 *  6. **`ECHO` latches, `MUTE` does not.** Riding the fader is a gesture and a gesture has
 *     to be short; opening a send is a state you can see. The echo is on the pads and the
 *     siren, never on the tune — putting the deck through the tank would need
 *     `createMediaElementSource`, which §9.6 refuses. The row prints `SEND` so the screen
 *     does not lie about what it touches.
 *  7. **`MUTE` uses `muted`, not `volume`.** WebKit ignores writes to
 *     `HTMLAudioElement.volume` outright, so a duck implemented as a volume ramp is
 *     silently inert on every iPhone. `wiring.setMuted` is the lever.
 *
 * The screen **fits** on a phone, and that is enforced in one place: `pads.module.css`
 * carries a `--fixed-h` budget listing every module that is not the bank, and the bank's
 * width — and therefore, through `aspect-ratio`, its height — is capped by what is left
 * of `--app-h`. It is allowed to overrun only when the 64px print floor binds, because a
 * pad that cannot print its own name is worse than a scroll (§9.9).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent } from 'react';

import type { PadAssignment, PadCategory, PadKit, PadSlot, PadSound } from '@bl/shared';
import { FACTORY_KIT, PAD_ROLES, PAD_SLOTS, SYNTH_PREFIX, hasCyrillic } from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import statesStyles from '../app/states/states.module.css';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { Chip } from '../components/primitives/Chip';
import { Halftone } from '../components/primitives/Halftone';
import { Ruler } from '../components/primitives/Ruler';
import { cx } from '../components/primitives/Rule';
import { Sheet } from '../components/primitives/Sheet';
import {
  disposePadEngine,
  ensurePadEngine,
  firePad,
  loadPadSounds,
  resumePadEngine,
  setEcho,
  sirenDown,
  sirenMove,
  sirenUp,
} from '../engine/pads/engine';
import type { ApiClient, ApiFailure } from '../lib/api';
import { api, toApiFailure } from '../lib/api';
import { telegram } from '../lib/telegram';
import type { useQueueStore } from '../store/queue';

import styles from './pads.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/**
 * The screen's name, and it is the SAME WORD the nav strip prints (`СЕМПЛЫ`).
 *
 * It read `Сирена` until 2026-09-06 — the loud object naming the whole screen — and the
 * operator hit exactly the defect that always causes: you tap a tab called one thing and
 * land on a page called another. A screen title that disagrees with the control that
 * opened it is a wayfinding bug, not a flourish.
 */
export const PADS_TITLE_RU = 'Семплы';
export const PADS_SUBTITLE_RU = 'Звук идёт поверх трека';
export const PADS_LIBRARY_TITLE_RU = 'Выбрать звук';
export const PADS_EMPTY_RU = 'Библиотека звуков пуста';
export const PADS_SIREN_HINT_RU = 'держи\nи веди';
export const PADS_LONGPRESS_RU = 'Долгое нажатие на пад — поменять звук';

/**
 * What the readout says when the platform has no `AudioContext` at all.
 *
 * A real state, not an error plate: the bank still prints, the library still opens and a
 * pad still takes a new sound — the screen is simply silent. §8.9's error plate is for a
 * request that failed, which this is not.
 */
export const PADS_NO_AUDIO_RU = 'Этот браузер не умеет Web Audio — звука не будет';

/** The way back to the bank as it ships. */
export const PADS_RESET_RU = 'Вернуть заводской';

/** The `SEND` cells. English panel print, Russian hint — §9.9. */
export const PADS_ECHO_HINT_RU = 'нажми — останется';
export const PADS_MUTE_HINT_RU = 'держи';

/** The library's category filter, in the order the sheet prints them. */
const CATEGORY_RU: Readonly<Record<PadCategory, string>> = Object.freeze({
  horns: 'Горны и сирены',
  drums: 'Барабаны',
  keys: 'Клавиши',
  fx: 'Эффекты',
  bass: 'Бас',
  calls: 'Свистки',
});

/**
 * The Russian name of a synth voice, for the readout lane.
 *
 * A synth slot has no library row to read a gloss off, so the seven names live here.
 * Keyed by the bare voice rather than the `synth:` reference, so the table cannot drift
 * from `SYNTH_VOICES` without a type error.
 */
const SYNTH_RU: Readonly<Record<string, string>> = Object.freeze({
  shot: 'Шот',
  'one-drop': 'Бочка',
  snare: 'Малый',
  tom: 'Том',
  cowbell: 'Ковбелл',
  bass: 'Бас',
  brake: 'Стоп ленты',
});

/** The panel print of a synth slot, for a pad whose sound is not a library row. */
const SYNTH_LABEL: Readonly<Record<string, string>> = Object.freeze({
  shot: 'SHOT',
  'one-drop': 'ONE DROP',
  snare: 'SNARE',
  tom: 'TOM',
  cowbell: 'COWBELL',
  bass: 'BASS',
  brake: 'BRAKE',
});

/* ══ tuning ════════════════════════════════════════════════════════════════════════ */

/**
 * How long a press has to last before the library opens.
 *
 * 1500 ms, not the platform-usual 400. The operator asked for it longer — at 400 ms the
 * sheet opened on presses he meant as hits, and on this screen a press IS a hit: the
 * sound fires immediately and the hold only decides whether the library follows. So the
 * cost of too short is a sheet in your face mid-performance, and the cost of too long is
 * nothing at all. When in doubt, longer.
 */
export const LONG_PRESS_MS = 1500;

/** Movement past which a press is a drag and not a press, in CSS pixels. */
export const LONG_PRESS_SLOP_PX = 10;

/**
 * Ticks on the siren's printed scale — §6.6's motif doing its sixth job as a pitch scale.
 *
 * 87, not 90: `Ruler`'s `barTicks()` floors any count to a whole number of three-tick
 * bars, so a round 90 would silently render 90 and a 88 would render 87 anyway. Picking a
 * multiple of three states the intent instead of relying on the rounding.
 */
export const SIREN_SCALE_TICKS = 87;

/* ══ helpers ═══════════════════════════════════════════════════════════════════════ */

/** `synth:one-drop` → `one-drop`; a library id → `null`. */
function synthVoiceOf(assignment: PadAssignment): string | null {
  if (assignment === null || !assignment.startsWith(SYNTH_PREFIX)) return null;
  return assignment.slice(SYNTH_PREFIX.length);
}

/** The panel print for whatever sits on a slot. Empty slots print nothing. */
function labelFor(assignment: PadAssignment, byId: ReadonlyMap<string, PadSound>): string {
  const voice = synthVoiceOf(assignment);
  if (voice !== null) return SYNTH_LABEL[voice] ?? voice.toUpperCase();
  if (assignment === null) return '';
  return byId.get(assignment)?.label ?? assignment.toUpperCase();
}

/** The Russian name for the readout lane. */
function glossFor(assignment: PadAssignment, byId: ReadonlyMap<string, PadSound>): string {
  const voice = synthVoiceOf(assignment);
  if (voice !== null) return SYNTH_RU[voice] ?? voice;
  if (assignment === null) return '';
  return byId.get(assignment)?.ru ?? assignment;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export interface PadsDeps {
  readonly client?: ApiClient;
  readonly wiring?: Pick<Wiring, 'setMuted'>;
  /**
   * Both accepted and deliberately unread.
   *
   * A strip above the siren used to print the loaded track's title and catalogue number.
   * The operator had it removed on 2026-09-06: the mini player two rows below already
   * carries that fact, and the strip was one of the two modules that pushed the `SEND`
   * row and the readout lane under the fold. The options stay in the shape so the three
   * call sites that inject a whole store set do not have to special-case this screen.
   */
  readonly player?: unknown;
  readonly queue?: typeof useQueueStore;
}

export type PadsProps = Partial<ScreenProps> & { readonly deps?: PadsDeps };

interface PadsState {
  readonly status: 'loading' | 'ready' | 'error';
  readonly sounds: readonly PadSound[];
  readonly kit: PadKit;
  readonly error: ApiFailure | null;
}

const INITIAL: PadsState = {
  status: 'loading',
  sounds: [],
  kit: FACTORY_KIT,
  error: null,
};

export function Pads({ deps = {} }: PadsProps): JSX.Element {
  const client = deps.client ?? api;

  const [state, setState] = useState<PadsState>(INITIAL);
  const [lit, setLit] = useState<Readonly<Record<string, boolean>>>({});
  const [echoOn, setEchoOn] = useState(false);
  const [muted, setMutedState] = useState(false);
  const [sirenLive, setSirenLive] = useState(false);
  const [readout, setReadout] = useState<string>(PADS_SUBTITLE_RU);
  const [picking, setPicking] = useState<PadSlot | null>(null);
  const [filter, setFilter] = useState<PadCategory | null>(null);

  const litTimers = useRef(new Map<string, number>());
  const pressRef = useRef<{
    slot: PadSlot;
    x: number;
    y: number;
    timer: number;
    long: boolean;
  } | null>(null);

  /* ── load ────────────────────────────────────────────────────────────────────── */

  const load = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const [sounds, kit] = await Promise.all([client.padSounds(), client.padKit()]);
      setState({ status: 'ready', sounds: sounds.sounds, kit, error: null });
    } catch (error) {
      setState({
        status: 'error',
        sounds: [],
        kit: FACTORY_KIT,
        error: toApiFailure(error),
      });
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Own the swipe gesture for as long as this screen is mounted, and give it back on the
   * way out — see rule 2 in the module header. Restoring on unmount rather than on
   * release is the whole point: the boot-time disable is already gone by the time a user
   * gets here.
   */
  useEffect(() => {
    telegram.disableVerticalSwipes();
    return () => {
      telegram.enableVerticalSwipes();
      disposePadEngine();
    };
  }, []);

  /*
   * Bring the context up and decode the whole library **as soon as the catalogue
   * arrives**, without waiting for a gesture.
   *
   * A context may be constructed and decoded into while suspended; only resuming needs a
   * gesture. Waiting for the first tap meant the first press of nine of the sixteen pads
   * landed on a buffer that had not been fetched yet — silent, and indistinguishable from
   * a broken pad. This is also where the iOS playback session gets claimed, which is the
   * other half of "the pads only sound once a track is playing".
   */
  const decodeStarted = useRef(false);
  useEffect(() => {
    if (decodeStarted.current || state.sounds.length === 0) return;
    if (ensurePadEngine() === null) {
      setReadout(PADS_NO_AUDIO_RU);
      return;
    }
    decodeStarted.current = true;
    const run = (): void => {
      void loadPadSounds(state.sounds, (file) => client.padSoundUrl(file));
    };
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback;
    if (typeof idle === 'function') idle(run);
    else setTimeout(run, 0);
  }, [client, state.sounds]);

  /* ── the engine, always entered through a gesture ─────────────────────────────── */

  /**
   * Resume the context, and take the swipe gesture back.
   *
   * Called as the FIRST statement of every pointer handler, with nothing awaited above
   * it — a webview grants the gesture's audio privilege to the synchronous part of the
   * handler and an `await` above the resume spends it.
   *
   * It returns nothing and guards nothing on purpose: a platform with no `AudioContext`
   * still has a screen worth operating — the library still opens, a pad still takes a new
   * sound, `ECHO` still latches — and only the sound is missing. `firePad` and `setEcho`
   * are already no-ops without an engine, so the gesture path needs no branch.
   */
  const wake = useCallback((): void => {
    /* First statement, nothing awaited above it. */
    ensurePadEngine();
    resumePadEngine();
    telegram.disableVerticalSwipes();
  }, []);

  /* ── pads ────────────────────────────────────────────────────────────────────── */

  const byId = useMemo(
    () => new Map(state.sounds.map((sound) => [sound.id, sound])),
    [state.sounds],
  );

  const ink = useCallback((slot: PadSlot, ms: number): void => {
    setLit((prev) => ({ ...prev, [slot]: true }));
    const timers = litTimers.current;
    const existing = timers.get(slot);
    if (existing !== undefined) clearTimeout(existing);
    timers.set(
      slot,
      setTimeout(
        () => {
          timers.delete(slot);
          setLit((prev) => ({ ...prev, [slot]: false }));
        },
        Math.max(120, ms),
      ) as unknown as number,
    );
  }, []);

  const hit = useCallback(
    (slot: PadSlot): void => {
      const assignment = state.kit.slots[slot] ?? null;
      if (assignment === null) return;
      const played = firePad(slot, assignment);
      if (!played.sounded) return;
      ink(slot, played.durationS * 1000);
      setReadout(`${slot} · ${glossFor(assignment, byId)}`);
      telegram.haptics.impact('light');
    },
    [byId, ink, state.kit.slots],
  );

  const onPadDown = useCallback(
    (slot: PadSlot) =>
      (event: ReactPointerEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        wake();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        /*
         * Fire immediately and open the library only if the finger stays. A pad that
         * waited 400 ms to find out whether this was a long press would be unplayable,
         * so the sound is never the thing that waits.
         */
        hit(slot);

        const timer = setTimeout(() => {
          const press = pressRef.current;
          if (press === null || press.slot !== slot) return;
          pressRef.current = { ...press, long: true };
          setPicking(slot);
          telegram.haptics.impact('medium');
        }, LONG_PRESS_MS) as unknown as number;

        pressRef.current = { slot, x: event.clientX, y: event.clientY, timer, long: false };
      },
    [hit, wake],
  );

  const onPadMove = useCallback((event: ReactPointerEvent<HTMLButtonElement>): void => {
    const press = pressRef.current;
    if (press === null || press.long) return;
    const moved =
      Math.abs(event.clientX - press.x) > LONG_PRESS_SLOP_PX ||
      Math.abs(event.clientY - press.y) > LONG_PRESS_SLOP_PX;
    if (!moved) return;
    clearTimeout(press.timer);
    pressRef.current = null;
  }, []);

  const onPadUp = useCallback((): void => {
    const press = pressRef.current;
    if (press === null) return;
    clearTimeout(press.timer);
    pressRef.current = null;
  }, []);

  /* ── the siren ───────────────────────────────────────────────────────────────── */

  const sirenRef = useRef<HTMLButtonElement | null>(null);

  const sweep = useCallback((event: ReactPointerEvent<HTMLButtonElement>): void => {
    const node = sirenRef.current;
    if (node === null) return;
    const box = node.getBoundingClientRect();
    const px = Math.max(0, Math.min(box.width, event.clientX - box.left));
    /* Written straight onto the node: a playhead through a render always lags the finger. */
    node.style.setProperty('--px', `${String(px)}px`);
    const readings = sirenMove(px / box.width, (event.clientY - box.top) / box.height);
    if (readings !== null) {
      setReadout(
        `Сирена · ${String(Math.round(readings.hz))} Гц · дрожание ` +
          `${readings.warbleHz.toFixed(1)} Гц`,
      );
    }
  }, []);

  const onSirenDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      wake();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      if (!sirenDown()) return;
      setSirenLive(true);
      telegram.haptics.impact('medium');
      sweep(event);
    },
    [sweep, wake],
  );

  const onSirenUp = useCallback((): void => {
    sirenUp();
    setSirenLive(false);
    setReadout('Сирена · эхо 333 мс');
  }, []);

  /* ── the sends ───────────────────────────────────────────────────────────────── */

  const toggleEcho = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      wake();
      const next = !echoOn;
      setEcho(next);
      setEchoOn(next);
      setReadout(next ? 'Эхо включено — бей по падам' : 'Эхо выключено');
      telegram.haptics.impact('light');
    },
    [echoOn, wake],
  );

  const duck = useCallback(
    (on: boolean): void => {
      const w = deps.wiring ?? getWiring();
      w.setMuted(on);
      setMutedState(on);
      setReadout(on ? 'Тюн приглушён' : 'Тюн вернулся');
    },
    [deps.wiring],
  );

  const onMuteDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      duck(true);
      telegram.haptics.impact('light');
    },
    [duck],
  );

  const onMuteUp = useCallback((): void => {
    if (!muted) return;
    duck(false);
  }, [duck, muted]);

  /* ── assignment ──────────────────────────────────────────────────────────────── */

  /**
   * Save a bank. `slot === null` means "restore the factory kit" — one write path rather
   * than a second endpoint, so there is exactly one place a kit can be persisted from.
   */
  const assign = useCallback(
    async (slot: PadSlot | null, soundId: PadAssignment): Promise<void> => {
      const slots: PadKit['slots'] =
        slot === null ? FACTORY_KIT.slots : { ...state.kit.slots, [slot]: soundId };
      /* Optimistic: the pad shows its new legend before the round trip lands, and the
         server's answer replaces it. A failed save reverts, and says so. */
      const previous = state.kit;
      setState((prev) => ({ ...prev, kit: { slots, updatedAt: 0 } }));
      setPicking(null);
      try {
        const saved = await client.putPadKit({ slots });
        setState((prev) => ({ ...prev, kit: saved }));
      } catch (error) {
        setState((prev) => ({ ...prev, kit: previous }));
        setReadout(toApiFailure(error).message);
      }
    },
    [client, state.kit],
  );

  /* ── render ──────────────────────────────────────────────────────────────────── */

  if (state.status === 'error') {
    return (
      <div className={styles.root}>
        <Halftone as="header" className={styles.masthead}>
          <h1 className="t-ui t-28" style={{ fontWeight: 700 }}>
            {PADS_TITLE_RU}
          </h1>
        </Halftone>
        <ErrorState failure={state.error as ApiFailure} grid="plate" onRetry={() => void load()} />
      </div>
    );
  }

  if (state.status === 'loading') {
    return (
      <div className={styles.root}>
        <Halftone as="header" className={styles.masthead}>
          <h1 className="t-ui t-28" style={{ fontWeight: 700 }}>
            {PADS_TITLE_RU}
          </h1>
        </Halftone>
        <Loading grid="plate" rows={4} />
      </div>
    );
  }

  const visible =
    filter === null ? state.sounds : state.sounds.filter((sound) => sound.category === filter);
  const categories = Array.from(new Set(state.sounds.map((sound) => sound.category)));

  return (
    <div className={styles.root}>
      <Halftone as="header" className={styles.masthead}>
        <span className={styles.mastheadLines}>
          <h1 className="t-ui t-28" style={{ fontWeight: 700 }}>
            {PADS_TITLE_RU}
          </h1>
          <p className={cx('t-mono', 't-11')} style={{ color: 'var(--text-3)' }}>
            BANK A · {String(PAD_SLOTS.length)} PADS
          </p>
        </span>
        {/*
          The way back. Without it a kit assembled by accident — which is exactly what a
          400 ms long-press produced — is permanent, and the operator has to re-find
          sixteen sounds by hand. It sends a `PUT` of the factory slots rather than a
          `DELETE`, so there is one write path and not two.

          It stands at the RIGHT END OF THE MASTHEAD LINE, not under it (2026-09-06). As
          a third stacked line it cost the screen 44px plus a gap, and those 52px are
          exactly what the `SEND` row and the readout lane were missing — a control you
          touch once a month was pushing two controls you touch every bar off the screen.
        */}
        <button
          type="button"
          className={cx('t-mono', 't-11', styles.reset)}
          onClick={() => {
            void assign(null, null);
          }}
        >
          {PADS_RESET_RU}
        </button>
      </Halftone>

      <StateFrame grid="plate" state="content" role="group" ariaLabel={PADS_TITLE_RU}>
        <div className={styles.screen}>
          <button
            ref={sirenRef}
            type="button"
            className={styles.siren}
            data-live={sirenLive ? 'true' : 'false'}
            aria-label="Сирена — держи палец и веди в сторону"
            onPointerDown={onSirenDown}
            onPointerMove={sirenLive ? sweep : undefined}
            onPointerUp={onSirenUp}
            onPointerCancel={onSirenUp}
            onLostPointerCapture={onSirenUp}
          >
            <span className={styles.sirenIndex} aria-hidden="true" />
            <span className={cx('t-display', 't-28', styles.sirenName)}>SIREN</span>
            <span className={cx('t-mono', 't-11', styles.sirenHint)}>
              держи
              <br />и веди
            </span>
            <Ruler variant="scale" count={SIREN_SCALE_TICKS} className={styles.sirenScale} />
          </button>

          <div className={styles.groupRule} aria-hidden="true">
            <span className={cx('t-mono', 't-11')} style={{ color: 'var(--text-3)' }}>
              BANK A
            </span>
            <span className={styles.line} />
            <span className={styles.drop} />
          </div>

          <div className={styles.bank} role="group" aria-label="Пады">
            {PAD_SLOTS.map((slot) => {
              const assignment = state.kit.slots[slot] ?? null;
              const label = labelFor(assignment, byId);
              return (
                <button
                  key={slot}
                  type="button"
                  className={cx(
                    styles.pad,
                    assignment === null && styles.empty,
                    lit[slot] === true && styles.lit,
                  )}
                  aria-label={`${slot} ${PAD_ROLES[slot]}${label === '' ? ' — пусто' : ` — ${label}`}`}
                  onPointerDown={onPadDown(slot)}
                  onPointerMove={onPadMove}
                  onPointerUp={onPadUp}
                  onPointerCancel={onPadUp}
                  onLostPointerCapture={onPadUp}
                >
                  <span className={cx('t-mono', 't-11', styles.padSlot)}>{slot}</span>
                  {/* The ROLE, silkscreened — not the loaded sound's own name. See
                      `PAD_ROLES` for why the two are different surfaces. */}
                  <span className={cx('t-mono', 't-11', styles.padLabel)}>{PAD_ROLES[slot]}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.groupRule} aria-hidden="true">
            <span className={cx('t-mono', 't-11')} style={{ color: 'var(--text-3)' }}>
              SEND
            </span>
            <span className={styles.line} />
          </div>

          <div className={styles.sends}>
            <button
              type="button"
              className={cx(styles.send, echoOn && styles.lit)}
              aria-pressed={echoOn}
              onPointerDown={toggleEcho}
            >
              <span className={cx('t-mono', 't-13')}>ECHO</span>
              <span className={cx('t-ui', 't-11', styles.sendHint)}>{PADS_ECHO_HINT_RU}</span>
            </button>
            <button
              type="button"
              className={cx(styles.send, muted && styles.lit)}
              onPointerDown={onMuteDown}
              onPointerUp={onMuteUp}
              onPointerCancel={onMuteUp}
              onLostPointerCapture={onMuteUp}
            >
              <span className={cx('t-mono', 't-13')}>MUTE</span>
              <span className={cx('t-ui', 't-11', styles.sendHint)}>{PADS_MUTE_HINT_RU}</span>
            </button>
          </div>

          <p
            className={cx(
              't-mono',
              't-13',
              styles.readout,
              hasCyrillic(readout) && statesStyles.cyr,
            )}
            data-readout="pads"
            aria-live="polite"
          >
            {readout}
          </p>
        </div>
      </StateFrame>

      <Sheet
        open={picking !== null}
        title={PADS_LIBRARY_TITLE_RU}
        count={`${String(visible.length)} / ${String(state.sounds.length)}`}
        onClose={() => setPicking(null)}
      >
        {state.sounds.length === 0 ? (
          <p className={cx('t-mono', 't-13', statesStyles.cyr)}>{PADS_EMPTY_RU}</p>
        ) : (
          <>
            <div className={styles.libFilters} role="group" aria-label="Группы звуков">
              <Chip selected={filter === null} onToggle={() => setFilter(null)} label="Все" />
              {categories.map((category) => (
                <Chip
                  key={category}
                  selected={filter === category}
                  onToggle={() => setFilter(category)}
                  label={CATEGORY_RU[category]}
                />
              ))}
            </div>
            <ul className={styles.libList}>
              {visible.map((sound) => {
                const isCurrent = picking !== null && state.kit.slots[picking] === sound.id;
                return (
                  <li key={sound.id} className={cx(styles.libRow, isCurrent && styles.current)}>
                    <button
                      type="button"
                      className={styles.libAudition}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        wake();
                        const played = firePad(PAD_SLOTS[0], sound.id);
                        if (played.sounded) telegram.haptics.impact('light');
                      }}
                    >
                      <span className={cx('t-mono', 't-13', styles.libLabel)}>{sound.label}</span>
                      <span className={cx('t-ui', 't-13', styles.libRu)}>{sound.ru}</span>
                    </button>
                    <button
                      type="button"
                      className={cx('t-mono', 't-11', styles.libAssign)}
                      onClick={() => {
                        if (picking === null) return;
                        void assign(picking, sound.id);
                      }}
                    >
                      Поставить
                    </button>
                    <span className={cx('t-mono', 't-11', styles.libLicence)}>
                      {sound.licence} · {sound.author}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Sheet>
    </div>
  );
}
